import { useEffect } from 'react';

const SHINE_SELECTOR = '.gold-shine, .eyebrow';

/**
 * Scroll-driven gold reflection. Each gold element's highlight band tracks
 * its position in the viewport, so it sweeps across as the element travels
 * up the screen and reverses when scrolling back. Elements inside the fixed
 * header can't use viewport position, so they follow page scroll progress.
 */
export function useGoldShine(routeKey: string) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      const max = document.documentElement.scrollHeight - vh;
      const pinned = `${((max > 0 ? window.scrollY / max : 0) * 360).toFixed(2)}%`;

      document.querySelectorAll<HTMLElement>(SHINE_SELECTOR).forEach((el) => {
        if (el.closest('header')) {
          el.style.setProperty('--shine-x', pinned);
          return;
        }
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const t = Math.max(0, Math.min(1, 1 - (rect.top + rect.height / 2) / vh));
        el.style.setProperty('--shine-x', `${(t * 100).toFixed(2)}%`);
      });
    };
    const schedule = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Content that appears later (demo output, accordion answers) gets painted too.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      observer.disconnect();
    };
  }, [routeKey]);
}

/** Fades `.reveal` blocks in as they enter the viewport. */
export function useReveal(routeKey: string) {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('js-reveal');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    document.querySelectorAll('.reveal:not(.is-in)').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [routeKey]);
}

/** Feeds the cursor position to `.gc-btn` so its spotlight follows the pointer. */
export function useButtonSpotlight() {
  useEffect(() => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    const onMove = (e: PointerEvent) => {
      const btn = (e.target as Element | null)?.closest?.<HTMLElement>('.gc-btn');
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      btn.style.setProperty('--mx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(2)}%`);
      btn.style.setProperty('--my', `${(((e.clientY - r.top) / r.height) * 100).toFixed(2)}%`);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => document.removeEventListener('pointermove', onMove);
  }, []);
}
